// Single source of truth for billing currencies on the frontend.
// Adding a currency = one entry in BILLING_CURRENCY_CONFIG, add the code to
// SUPPORTED_BILLING_CURRENCIES, and add its label key under
// BILLING_SETTINGS.CURRENCY.OPTIONS in the locale files.

export const DEFAULT_BILLING_CURRENCY = 'usd';

// Order here drives the order of the currency toggle in the UI.
export const SUPPORTED_BILLING_CURRENCIES = ['usd', 'brl'];

export const BILLING_CURRENCY_CONFIG = {
  usd: {
    code: 'usd',
    intlLocale: 'en-US',
    i18nLabelKey: 'BILLING_SETTINGS.CURRENCY.OPTIONS.USD',
  },
  brl: {
    code: 'brl',
    intlLocale: 'pt-BR',
    i18nLabelKey: 'BILLING_SETTINGS.CURRENCY.OPTIONS.BRL',
  },
  // Nepal buys through the in-app lane, not Stripe, so npr is a display currency
  // only and deliberately stays out of SUPPORTED_BILLING_CURRENCIES.
  npr: {
    code: 'npr',
    intlLocale: 'en-NP',
    i18nLabelKey: 'BILLING_SETTINGS.CURRENCY.OPTIONS.NPR',
  },
};

// Which currency a customer is quoted in follows the region their lane belongs to.
export const BILLING_REGION_CURRENCY = { nepal: 'npr' };

export const currencyForBillingRegion = region =>
  BILLING_REGION_CURRENCY[region] || DEFAULT_BILLING_CURRENCY;

export const getCurrencyConfig = code =>
  BILLING_CURRENCY_CONFIG[(code || DEFAULT_BILLING_CURRENCY).toLowerCase()] ||
  BILLING_CURRENCY_CONFIG[DEFAULT_BILLING_CURRENCY];

export const formatCurrencyAmount = (amount, code, options = {}) => {
  const { intlLocale, code: currencyCode } = getCurrencyConfig(code);
  return new Intl.NumberFormat(intlLocale, {
    style: 'currency',
    currency: currencyCode.toUpperCase(),
    ...options,
  }).format(amount);
};

// A plan can carry any feature flag, so the subscription card leads with the ones a
// customer recognises and drops the long tail once the list gets unwieldy.
export const MAX_PLAN_FEATURES_SHOWN = 12;

const PLAN_FEATURE_ORDER = [
  'captain_integration',
  'campaigns',
  'help_center',
  'sla',
  'audit_logs',
  'custom_roles',
  'automations',
  'reports',
  'advanced_assignment',
  'api_and_webhooks',
  'crm',
  'companies',
  'tickets',
  'macros',
  'advanced_search',
  'saml',
];

// Flags whose titleized form reads wrong. Mirrors the display names in config/features.yml,
// except for Captain, which this install ships as Tola AI.
const PLAN_FEATURE_LABELS = {
  api_and_webhooks: 'API and Webhooks',
  captain_integration: 'Tola AI',
  captain_integration_v2: 'Tola AI',
  crm: 'CRM',
  saml: 'SAML',
  sla: 'SLA',
  whatsapp_campaign: 'WhatsApp Campaign',
};

const titleize = flag =>
  flag
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const formatPlanFeature = flag =>
  PLAN_FEATURE_LABELS[flag] || titleize(flag);

// Unknown flags all rank last and keep the order the plan listed them in.
export const planFeatureLabels = (features = []) =>
  [...features]
    .sort(
      (a, b) =>
        (PLAN_FEATURE_ORDER.indexOf(a) + 1 || PLAN_FEATURE_ORDER.length + 1) -
        (PLAN_FEATURE_ORDER.indexOf(b) + 1 || PLAN_FEATURE_ORDER.length + 1)
    )
    .slice(0, MAX_PLAN_FEATURES_SHOWN)
    .map(formatPlanFeature);
